import window from 'window-or-global';

import { loadResources, requireLegacyWRM } from '@confluence/wrm';

import { scrollingElement } from '../tech-debt/facades/document';

const TICK = 1000 / 60;

/***
 * A shorthand method for creating a detached async script element that can manually be added to the DOM.
 * @param attributes
 * @returns {Element}
 */
export function createSyncScriptElement(attributes) {
	const element = createElement('script', attributes);
	element.async = false;
	return element;
}

/***
 * A shorthand method for creating a detached stylesheet that can manually be added to the DOM.
 * @param attributes
 * @returns {Element}
 */
export function createStyleElement(attributes) {
	return createElement('link', { rel: 'stylesheet', ...attributes });
}

/***
 * A shorthand method for document.createElement.
 * @param type
 * @param attributes
 * @returns {Element}
 */
export function createElement(type, attributes) {
	const element = document.createElement(type);
	Object.keys(attributes).forEach((attributeKey) =>
		element.setAttribute(attributeKey, attributes[attributeKey]),
	);
	return element;
}

export function scrollTo(element, to, duration, callback) {
	if (duration <= 0 || !duration || to === element.scrollTop) {
		element.scrollTop = to;
		if (callback) {
			callback();
		}
		return;
	}

	const difference = to - element.scrollTop;
	const perTick = (difference / duration) * TICK;

	let scrollTop = element.scrollTop;
	const animationStep = () => {
		const newPosition = scrollTop + perTick;
		if ((difference < 0 && newPosition <= to) || (difference > 0 && newPosition >= to)) {
			element.scrollTop = to;
			if (callback) {
				callback();
			}
			return;
		} else {
			scrollTop = newPosition;
		}
		element.scrollTop = scrollTop;
		window.requestAnimationFrame(animationStep);
	};
	window.requestAnimationFrame(animationStep);
}

export function scrollTop(element, duration, callback) {
	scrollTo(element, 0, duration, callback);
}

export function scrollElementToViewport(el, config = {}) {
	if (!el) {
		return;
	}

	if (!config.container) {
		config.container = scrollingElement();
	}

	const { container, topGap = 0, bottomGap = 0, duration = 0 } = config;

	const rect = el.getBoundingClientRect();
	const { top, bottom } = rect;
	const { clientHeight, scrollTop } = config.container;

	const pixelsCutFromTop = -top + topGap;
	const pixelsCutFromBottom = bottom - clientHeight + bottomGap;

	if (pixelsCutFromTop > 0) {
		return scrollTo(container, scrollTop - pixelsCutFromTop, duration);
	}

	if (pixelsCutFromBottom > 0) {
		return scrollTo(container, scrollTop + pixelsCutFromBottom, duration);
	}
}

export function hide(element) {
	element.style.display = 'none';
}

export function show(element) {
	element.style.display = 'block';
}

function applyClassChanges(element, className, action) {
	if (!element) {
		return;
	}
	className.split(' ').forEach((item) => element.classList[action](item));
}

export function addClass(element, className) {
	applyClassChanges(element, className, 'add');
}

export function removeClass(element, className) {
	applyClassChanges(element, className, 'remove');
}

/* istanbul ignore next */
export function getBodyNode() {
	return typeof document !== 'undefined' && document.body ? document.body : {};
}

/* istanbul ignore next */
export function getHtmlNode() {
	return getBodyNode().parentNode || {};
}

/* istanbul ignore next */
export function isElementVisible(element) {
	return !!element.offsetParent;
}

/* istanbul ignore next */
export function isVisibleInViewPort(element, container) {
	if (!element || !document.documentElement) {
		return;
	}

	const boundaries = element.getBoundingClientRect();
	const { top, bottom } = boundaries;

	let viewportHeight;
	let limitTop = 0;

	if (container) {
		const containerBoundaries = container.getBoundingClientRect();
		limitTop = containerBoundaries.top;
		viewportHeight = container.offsetHeight;
	} else {
		viewportHeight = document.documentElement.offsetHeight;
	}

	const topIsVisible = top >= limitTop && top < viewportHeight + limitTop;
	const bottomIsVisible = bottom > limitTop && bottom <= viewportHeight + limitTop;

	return topIsVisible && bottomIsVisible;
}

/**
 * Check one part of element is still in viewport or not.
 * @param {object} element - a target element
 * @param {object} documentObject - document object which makes writing tests easier.
 * @returns {boolean}
 */
export function isPartOfElementStillInViewPort(element, documentObject) {
	const docEl = documentObject ? documentObject.documentElement : document.documentElement;
	if (!element || !docEl) {
		return;
	}

	const boundaries = element.getBoundingClientRect();
	const { top, bottom } = boundaries;
	const viewportHeight = docEl.offsetHeight;

	const topIsVisible = 0 <= top && top < viewportHeight;
	const bottomIsVisible = 0 <= bottom && bottom <= viewportHeight;
	const isBiggerThanViewPort = top <= 0 && 0 <= bottom && bottom >= viewportHeight;
	return topIsVisible || bottomIsVisible || isBiggerThanViewPort;
}

/**
 * Check top of element is still in viewport or not
 * @param element
 * @param documentObject
 * @returns {boolean}
 */
export function isTopOfElementStillInViewPort(element, documentObject) {
	const docEl = documentObject ? documentObject.documentElement : document.documentElement;
	if (!element || !docEl) {
		return;
	}

	const boundaries = element.getBoundingClientRect();
	const { top } = boundaries;
	const viewportHeight = docEl.offsetHeight;

	// topIsVisible
	return 0 <= top && top < viewportHeight;
}

/* istanbul ignore next */
export function scrollIntoView(element, container) {
	container = container || document.scrollingElement || document.body.parentNode;
	if (!element || isVisibleInViewPort(element, container)) {
		return;
	}

	const containerOffset = container.getBoundingClientRect().top;
	const containerScrollTop = container.scrollTop;

	container.scrollTop = element.getBoundingClientRect().top - containerOffset + containerScrollTop;
}

export function getElementTop(element) {
	let top = element.offsetTop;
	while (element.offsetParent) {
		element = element.offsetParent;
		top += element.offsetTop;
	}
	return top;
}

export function isElementInViewport(element, container) {
	if (!element) {
		return false;
	}
	container = container || document.scrollingElement || document.body.parentNode;
	const height = element.offsetHeight;
	const top = getElementTop(element);

	const containerTop = getElementTop(container) + container.scrollTop;
	const containerHeight = container.offsetHeight;
	return top >= containerTop && top + height <= containerTop + containerHeight;
}

/**
 * Method to find scrollable parent for DOM node.
 * It'll search for the first parent node that has scroll available in it.
 * Node has scroll available once its `scrollHeight` is more than `clientHeight` and
 * `overflow` style is not `hidden` or `visible`
 *
 * @param node - DOM node
 * @returns {*}
 */
export function getScrollableParent(node) {
	if (!node || node === document) {
		return null;
	}

	const nodeStyle = window.getComputedStyle(node);
	if (
		node.scrollHeight > node.clientHeight &&
		node.style.overflow !== 'hidden' &&
		nodeStyle.overflow !== 'visible'
	) {
		return node;
	} else {
		return getScrollableParent(node.parentNode);
	}
}

export function scrollElementToViewCenter(element, container) {
	container = container || getScrollableParent(element);
	if (!element || !container || isElementInViewport(element, container)) {
		return;
	}
	container.scrollTop =
		getElementTop(element) - getElementTop(container) - container.clientHeight / 2;
}

/**
 * Load JS dependencies and inject them to the script container
 * Note: Load JS resources only when superbatch has been loaded
 *
 * @param resourceKeys Web resource keys
 * @param jsTags JS tags
 * @param scriptContainer The DOM element that will contain the JS script tags
 * @returns {Promise}
 */
export const loadJSDependencies = (resourceKeys, jsTags, scriptContainer) => {
	if (!scriptContainer) {
		return Promise.reject(new Error('script container is not present'));
	}

	if (resourceKeys.length) {
		return new Promise((resolve) => {
			requireLegacyWRM(
				resourceKeys,
				() => {
					loadResources(scriptContainer, jsTags).then(resolve);
				},
				() => {
					if (process.env.NODE_ENV !== 'production') {
						console.error('Cannot load resources because WRM is not available');
					}
				},
			);
		});
	} else {
		return loadResources(scriptContainer, jsTags);
	}
};
